import type { FormHTMLAttributes, ReactNode } from 'react';
import { Button, Spinner } from '../primitives/primitives.js';
import { Icon } from '../icons/icons.js';
import styles from './layout.module.css';

/** TESSMA ONE D-8 form composition — STATUS: APPROVED AND FROZEN (v1.1). */

export function Form({ children, className, ...rest }: FormHTMLAttributes<HTMLFormElement>) {
  return (
    <form className={[styles.form, className ?? ''].filter(Boolean).join(' ')} noValidate {...rest}>
      {children}
    </form>
  );
}

export interface FormSectionProps {
  readonly title: string;
  readonly description?: ReactNode;
  readonly children: ReactNode;
}

export function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <section className={styles.section} aria-label={title}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        {description !== undefined && <p className={styles.sectionDescription}>{description}</p>}
      </div>
      <div className={styles.stack}>{children}</div>
    </section>
  );
}

export function FormRow({
  columns = 2,
  children,
}: {
  readonly columns?: 2 | 3 | 'auto';
  readonly children: ReactNode;
}) {
  const columnClass = columns === 2 ? styles.rowTwo : columns === 3 ? styles.rowThree : '';
  return <div className={[styles.row, columnClass].filter(Boolean).join(' ')}>{children}</div>;
}

export function FormInline({ children }: { readonly children: ReactNode }) {
  return <div className={styles.inline}>{children}</div>;
}

export interface FormActionsProps {
  readonly submitLabel?: string;
  readonly cancelLabel?: string;
  readonly onCancel?: () => void;
  readonly submitting?: boolean;
  readonly note?: ReactNode;
  readonly destructive?: ReactNode;
}

export function FormActions({
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  onCancel,
  submitting = false,
  note,
  destructive,
}: FormActionsProps) {
  return (
    <div className={styles.actions}>
      {(note !== undefined || destructive !== undefined) && (
        <div className={styles.actionsLeft}>
          {destructive}
          {note}
        </div>
      )}
      {onCancel !== undefined && (
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          {cancelLabel}
        </Button>
      )}
      <Button type="submit" variant="primary" disabled={submitting}>
        {submitting && <Spinner size={13} />}
        {submitting ? 'Saving…' : submitLabel}
      </Button>
    </div>
  );
}

export interface UnsavedChangesBarProps {
  readonly message?: string;
  readonly onDiscard: () => void;
  readonly onSave: () => void;
  readonly saving?: boolean;
}

export function UnsavedChangesBar({
  message = 'You have unsaved changes.',
  onDiscard,
  onSave,
  saving = false,
}: UnsavedChangesBarProps) {
  return (
    <div className={styles.unsaved} role="status">
      <Icon name="warning" size={15} />
      {message}
      <span className={styles.unsavedActions}>
        <Button variant="secondary" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </span>
    </div>
  );
}
